import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import type {
  DispatchAssignmentRead,
  DispatchTripRead,
} from 'src/common/external/bambi-api/bambiApi';
import type { ApplyRoutesResults } from 'src/common/external/bambi-api/routesApi';

import { convertLocalDaysToDaysOfWeek } from './forms/daysOfWeekMaps';
import type { RouteTemplateFormValues } from './forms/types';

export interface RouteTemplateSliceState {
  showRouteTemplateForm: boolean;
  editingRouteTemplate?: Partial<RouteTemplateFormValues>;
  // During bulk creation store assignments and trips
  bulkCreationAssignments: DispatchAssignmentRead[];
  bulkCreationTrips: DispatchTripRead[];
  selectedTrips: Record<string, boolean>;
  workflow: 'single' | 'bulk';
  selectedAssignment?: DispatchAssignmentRead;
  showApplyRouteTemplatesModal: boolean;
  applyRouteTemplatesResult: ApplyRoutesResults | null;

  filterSearch: string;
  filterDriver: string[];
  filterRangeStart: string | undefined;
  filterRangeEnd: string | undefined;
  filterDaysOfWeek: string[];
}

export const initialState: RouteTemplateSliceState = {
  bulkCreationAssignments: [],
  bulkCreationTrips: [],
  showRouteTemplateForm: false,
  selectedTrips: {},
  workflow: 'single',
  showApplyRouteTemplatesModal: false,
  applyRouteTemplatesResult: null,

  filterSearch: '',
  filterDriver: [],
  filterRangeStart: '',
  filterRangeEnd: '',
  filterDaysOfWeek: convertLocalDaysToDaysOfWeek([
    DateTime.now().toFormat('cccc'),
  ]),
};

export const routeTemplatesSlice = createSlice({
  name: 'routeTemplates',
  initialState,
  selectors: {
    hasFilters: (state) =>
      !!state.filterSearch ||
      state.filterDriver.length > 0 ||
      !!state.filterRangeStart ||
      !!state.filterRangeEnd ||
      state.filterDaysOfWeek.length > 0,
  },
  reducers: {
    setShowRouteTemplateForm(state, action: PayloadAction<boolean>) {
      state.showRouteTemplateForm = action.payload;
    },
    setEditingRouteTemplate(
      state,
      action: PayloadAction<Partial<RouteTemplateFormValues> | undefined>
    ) {
      state.editingRouteTemplate = action.payload;
    },
    setBulkCreationData(
      state,
      action: PayloadAction<{
        bulkCreationAssignments: DispatchAssignmentRead[];
        bulkCreationTrips: DispatchTripRead[];
      }>
    ) {
      state.bulkCreationAssignments = action.payload.bulkCreationAssignments;
      state.bulkCreationTrips = action.payload.bulkCreationTrips;
      state.workflow = 'bulk';
      state.selectedAssignment = action.payload.bulkCreationAssignments[0];
    },
    selectTrip(state, action: PayloadAction<string>) {
      state.selectedTrips[action.payload] = true;
    },
    deselectTrip(state, action: PayloadAction<string>) {
      state.selectedTrips[action.payload] = false;
    },
    resetSelectedTrips(state, action: PayloadAction<undefined>) {
      state.selectedTrips = {};
    },
    selectAssignment(
      state,
      action: PayloadAction<DispatchAssignmentRead | undefined>
    ) {
      state.selectedAssignment = action.payload;
      state.selectedTrips = {};
    },
    setBulkCreationAssignments(
      state,
      action: PayloadAction<DispatchAssignmentRead[]>
    ) {
      state.bulkCreationAssignments = action.payload;
      state.selectedAssignment = action.payload[0];
    },
    setShowApplyRouteTemplatesModal(state, action: PayloadAction<boolean>) {
      state.showApplyRouteTemplatesModal = action.payload;
    },
    setApplyRouteTemplatesResult(
      state,
      action: PayloadAction<ApplyRoutesResults | null>
    ) {
      state.applyRouteTemplatesResult = action.payload;
    },
    setWorkflow(state, action: PayloadAction<'single' | 'bulk'>) {
      state.workflow = action.payload;
    },
    setFilterSearch(state, action: PayloadAction<string>) {
      state.filterSearch = action.payload;
    },
    setFilterDriver(state, action: PayloadAction<string[]>) {
      state.filterDriver = action.payload;
    },
    setFilterRangeStart(state, action: PayloadAction<string | undefined>) {
      state.filterRangeStart = action.payload;
    },
    setFilterRangeEnd(state, action: PayloadAction<string | undefined>) {
      state.filterRangeEnd = action.payload;
    },
    setFilterDaysOfWeek(state, action: PayloadAction<string[]>) {
      state.filterDaysOfWeek = action.payload;
    },
    clearFilters(state, action: PayloadAction<undefined>) {
      state.filterSearch = '';
      state.filterDriver = [];
      state.filterRangeStart = '';
      state.filterRangeEnd = '';
      state.filterDaysOfWeek = [];
    },
  },
});

export const routeTemplateActions = routeTemplatesSlice.actions;
export const routeTemplateSelectors = routeTemplatesSlice.selectors;
